import { SourceRowProps } from 'screens/Campaign/CampaignContext/Types'
import { SourceMaterialsMediaObject, Website } from 'types'
import Styles from 'screens/Campaign/CampaignContext/CampaignContext.module.scss'
import { Text } from 'blaze-ui/Text/Text'
import dayjs from 'dayjs'
import { Pill } from 'components/Pill/Pill'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { Browser, Document, Trash2 } from 'blaze-ui/icons'
import { Voice } from 'blaze-ui/icons/16'
import { Image03, Pdf, Video } from 'blaze-ui/icons/24'
import React, { ImgHTMLAttributes, useEffect, useState } from 'react'

const Favicon = (props: ImgHTMLAttributes<HTMLImageElement>) => {
  const { src, alt, ...rest } = props
  const [error, setError] = useState(false)

  useEffect(() => {
    if (!src) return

    const img = new Image()
    img.src = src
    img.onerror = () => {
      setError(true)
    }
  }, [src])

  return (
    <>
      {!error && src && <img alt={alt} src={src} {...rest} />}
      {(error || !src) && (
        <div className={Styles.defaultIcon}>
          <Browser size={14} color="var(--dark-40)" />
        </div>
      )}
    </>
  )
}

const WebsiteRow = ({ source, createdAt, onDelete }: { source: Website; createdAt: string; onDelete: () => void }) => {
  const { url, title } = source

  return (
    <tr className={Styles.row}>
      <td className={Styles.cell}>
        <div className={Styles.icon}>
          <Favicon src={source.favicon} alt="Website Logo" />
        </div>
        <div className={Styles.titleContainer}>
          <Text variant="smallList" color="var(--dark-80)" lineClamp={1}>
            {title || url}
          </Text>
          {title && (
            <Text variant="placeholder" lineClamp={1}>
              {url}
            </Text>
          )}
        </div>
      </td>
      <td className={Styles.cell}>
        <Pill>Website</Pill>
      </td>
      <td className={Styles.cell}>
        <Text variant="secondary" lineClamp={1}>
          {dayjs(createdAt).format('ll')}
        </Text>
      </td>
      <td className={Styles.cell}>
        <IconButton icon={Trash2} variant="ghost" color="var(--dark-80)" size="sm" onPress={onDelete} />
      </td>
    </tr>
  )
}

function humanFilename(url: string): string {
  try {
    const pathname = new URL(url).pathname
    const encoded = pathname.split('/').pop() || url
    const decoded = decodeURIComponent(encoded)
    // Strip upload hash suffix: "name-abc123def456.ext" -> "name.ext"
    return decoded.replace(/-[0-9a-f]{10,}\.([\w]+)$/, '.$1')
  } catch {
    return url
  }
}

const IMAGE_EXTENSIONS = new Set([
  'png',
  'jpg',
  'jpeg',
  'gif',
  'webp',
  'svg',
  'bmp',
  'ico',
  'heic',
  'avif',
  'tif',
  'tiff',
])
const VIDEO_EXTENSIONS = new Set(['mp4', 'mpeg', 'webm', 'mov', 'avi', 'mkv', 'm4v'])
const AUDIO_EXTENSIONS = new Set(['mp3', 'wav', 'm4a', 'aac', 'ogg', 'flac'])

function extensionFromSourceUrl(sourceUrl: string): string {
  const name = humanFilename(sourceUrl).toLowerCase()
  const dot = name.lastIndexOf('.')
  return dot >= 0 ? name.slice(dot + 1) : ''
}

/** MIME is sometimes missing or generic; infer from URL so ContextTable matches real file type. */
function fileRowDisplay(sourceUrl: string, mimeType?: string) {
  const mt = (mimeType || '').toLowerCase().trim()
  const primary = mt.split('/')[0]
  const sub = mt.split('/')[1] || ''

  if (primary === 'image') return { pill: 'Image' as const, icon: 'image' as const }
  if (primary === 'video') return { pill: 'Video' as const, icon: 'video' as const }
  if (primary === 'audio') return { pill: 'Audio' as const, icon: 'audio' as const }
  if (sub === 'pdf' || mt === 'application/pdf') return { pill: 'PDF' as const, icon: 'pdf' as const }
  if (primary === 'text') return { pill: 'Document' as const, icon: 'document' as const }

  const ext = extensionFromSourceUrl(sourceUrl)
  if (IMAGE_EXTENSIONS.has(ext)) return { pill: 'Image' as const, icon: 'image' as const }
  if (VIDEO_EXTENSIONS.has(ext)) return { pill: 'Video' as const, icon: 'video' as const }
  if (AUDIO_EXTENSIONS.has(ext)) return { pill: 'Audio' as const, icon: 'audio' as const }
  if (ext === 'pdf') return { pill: 'PDF' as const, icon: 'pdf' as const }

  return { pill: 'Document' as const, icon: 'document' as const }
}

const FileRow = ({
  source,
  createdAt,
  onDelete,
}: {
  source: SourceMaterialsMediaObject
  createdAt: string
  onDelete: () => void
}) => {
  const { source_url, mime_type } = source

  const { pill: sourceTypePill, icon: iconKind } = fileRowDisplay(source_url, mime_type)

  let icon = <Document size={24} />
  if (iconKind === 'image') {
    icon = <Image03 size={24} color="var(--dark-80)" />
  } else if (iconKind === 'audio') {
    icon = <Voice size={24} />
  } else if (iconKind === 'video') {
    icon = <Video size={24} />
  } else if (iconKind === 'pdf') {
    icon = <Pdf color="var(--red-50)" size={24} />
  }

  return (
    <tr className={Styles.row}>
      <td className={Styles.cell}>
        <div className={Styles.icon}>{icon}</div>
        <Text variant="smallList" lineClamp={1} color="var(--dark-80)">
          {humanFilename(source_url)}
        </Text>
      </td>
      <td className={Styles.cell}>
        <Pill>{sourceTypePill}</Pill>
      </td>
      <td className={Styles.cell}>
        <Text variant="secondary" color="var(--dark-80)">
          {dayjs(createdAt).format('ll')}
        </Text>
      </td>
      <td className={Styles.cell}>
        <IconButton icon={Trash2} variant="ghost" color="var(--dark-80)" size="sm" onPress={onDelete} />
      </td>
    </tr>
  )
}

export const SourceRow = ({ source, onDeleteSource }: SourceRowProps) => {
  const { type, created_at } = source

  switch (type) {
    case 'Website':
      return <WebsiteRow source={source} createdAt={created_at} onDelete={() => onDeleteSource(source)} />
    case 'SourceMaterialsMediaObject':
      return <FileRow source={source} createdAt={created_at} onDelete={() => onDeleteSource(source)} />
    default:
      return null
  }
}
